import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { 
  LayoutTemplate, 
  Church, 
  Music, 
  Presentation, 
  Star, 
  Headphones, 
  Mic,
  Check,
  Plus,
  Save,
  Trash2,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { templatesAPI, equipmentAPI } from "@/lib/api";

// Icon mapping for template categories
const categoryIcons = {
  church: Church,
  iglesia: Church,
  music: Music,
  musica: Music,
  presentation: Presentation,
  corporativo: Presentation,
  star: Star,
  concierto: Star,
};

const getCategoryIcon = (category, icon) => {
  const IconComponent = categoryIcons[icon] || categoryIcons[category] || LayoutTemplate;
  return IconComponent;
};

const categoryColors = {
  iglesia: "from-purple-500/20 to-purple-600/10 border-purple-500/30",
  musica: "from-pink-500/20 to-pink-600/10 border-pink-500/30",
  corporativo: "from-blue-500/20 to-blue-600/10 border-blue-500/30",
  concierto: "from-yellow-500/20 to-yellow-600/10 border-yellow-500/30",
  custom: "from-green-500/20 to-green-600/10 border-green-500/30",
};

export default function TemplatesPage({ user, onLogout }) {
  const [templates, setTemplates] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applying, setApplying] = useState(false);
  
  const [applyForm, setApplyForm] = useState({
    equipment_id: "",
    base_frequency: "560.0",
    band_id: "default"
  });
  
  const [saveForm, setSaveForm] = useState({
    name: "",
    description: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, customRes, equipmentRes] = await Promise.all([
        templatesAPI.getAll(),
        templatesAPI.getCustom(),
        equipmentAPI.getAll()
      ]);
      setTemplates(templatesRes.data.templates || []);
      setCustomTemplates(customRes.data.templates || []);
      setEquipmentList(equipmentRes.data.equipment || []);
    } catch (error) {
      // Error handled silently
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setApplyForm({
      equipment_id: template.systems?.[0]?.equipment_id || "anleon_s3d",
      base_frequency: "560.0",
      band_id: "default"
    });
    setShowApplyDialog(true);
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    
    setApplying(true);
    try {
      const response = await templatesAPI.apply({
        template_id: selectedTemplate.id,
        equipment_id: applyForm.equipment_id || undefined,
        base_frequency: parseFloat(applyForm.base_frequency),
        band_id: applyForm.band_id
      });
      
      toast.success(`Plantilla "${selectedTemplate.name}" aplicada: ${response.data.created_count} sistemas creados`);
      setShowApplyDialog(false);
      setSelectedTemplate(null);
    } catch (error) {
      // Error handled silently
      toast.error(error.response?.data?.detail || "Error al aplicar plantilla");
    } finally {
      setApplying(false);
    }
  };

  const handleSaveCustomTemplate = async () => {
    if (!saveForm.name.trim()) {
      toast.error("Ingresa un nombre para la plantilla");
      return;
    }
    
    try {
      await templatesAPI.saveCustom({
        name: saveForm.name,
        description: saveForm.description
      });
      toast.success("Plantilla guardada exitosamente");
      setShowSaveDialog(false);
      setSaveForm({ name: "", description: "" });
      fetchData();
    } catch (error) {
      // Error handled silently
      toast.error(error.response?.data?.detail || "Error al guardar plantilla");
    }
  };

  const handleDeleteCustomTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      await templatesAPI.deleteCustom(selectedTemplate.id);
      toast.success("Plantilla eliminada");
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      fetchData();
    } catch (error) {
      // Error handled silently
      toast.error("Error al eliminar plantilla");
    }
  };

  const TemplateCard = ({ template, isCustom = false }) => {
    const IconComponent = getCategoryIcon(template.category, template.icon);
    const colorClass = categoryColors[template.category] || categoryColors.custom;
    
    return (
      <div 
        className={`relative p-5 rounded-lg border bg-gradient-to-br ${colorClass} hover:border-white/30 transition-all cursor-pointer group`}
        onClick={() => handleSelectTemplate(template)}
        data-testid={`template-card-${template.id}`}
      >
        {isCustom && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTemplate(template);
              setShowDeleteDialog(true);
            }}
            className="absolute top-3 right-3 p-1.5 rounded bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/30"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-white/10">
            <IconComponent className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-lg">{template.name}</h3>
            <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-2">{template.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2 text-sm">
            <Headphones className="w-4 h-4 text-[#34C759]" />
            <span className="text-[#34C759] font-medium">{template.total_iem} IEM</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Mic className="w-4 h-4 text-[#8B5CF6]" />
            <span className="text-[#8B5CF6] font-medium">{template.total_mic} Mic</span>
          </div>
          <div className="ml-auto">
            <ChevronRight className="w-5 h-5 text-[#71717A] group-hover:text-white transition-colors" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Plantillas de Eventos
          </h1>
          <p className="text-sm text-[#71717A]">
            Configuraciones predefinidas para diferentes tipos de eventos
          </p>
        </div>
        <Button
          onClick={() => setShowSaveDialog(true)}
          className="btn-tactical btn-tactical-primary"
          data-testid="save-template-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          Guardar Config. Actual
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#34C759]"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Pre-defined Templates */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-[#34C759]" />
              Plantillas Predefinidas
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          </div>

          {/* Custom Templates */}
          {customTemplates.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-[#FFCC00]" />
                Mis Plantillas Personalizadas
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {customTemplates.map((template) => (
                  <TemplateCard key={template.id} template={template} isCustom />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Apply Template Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-[#34C759]" />
              Aplicar Plantilla
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              {selectedTemplate?.name} - {selectedTemplate?.total_iem} IEM + {selectedTemplate?.total_mic} Mic
            </DialogDescription>
          </DialogHeader>

          {selectedTemplate && (
            <div className="space-y-4">
              {/* Template Preview */}
              <div className="p-4 bg-[#0A0A0A] border border-white/[0.05] rounded-lg max-h-48 overflow-y-auto">
                <p className="text-xs text-[#71717A] mb-2">Sistemas a crear:</p>
                <div className="space-y-1">
                  {selectedTemplate.systems?.map((sys, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-white">{sys.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        sys.type === 'iem' 
                          ? 'bg-[#34C759]/20 text-[#34C759]' 
                          : 'bg-[#8B5CF6]/20 text-[#8B5CF6]'
                      }`}>
                        {sys.type === 'iem' ? 'IEM' : 'MIC'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Configuration */}
              <div className="space-y-4">
                <div>
                  <Label className="text-[#A1A1AA]">Equipo a usar</Label>
                  <Select 
                    value={applyForm.equipment_id} 
                    onValueChange={(v) => setApplyForm({...applyForm, equipment_id: v})}
                  >
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                      <SelectValue placeholder="Seleccionar equipo" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111111] border-white/10">
                      {equipmentList.map((eq) => (
                        <SelectItem key={eq.id} value={eq.id} className="text-white">
                          {eq.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#A1A1AA]">Frecuencia Base (MHz)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={applyForm.base_frequency}
                    onChange={(e) => setApplyForm({...applyForm, base_frequency: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  />
                  <p className="text-xs text-[#71717A] mt-1">
                    Las frecuencias se asignarán con 0.5 MHz de espaciado
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button
                  variant="ghost"
                  onClick={() => setShowApplyDialog(false)}
                  className="text-[#A1A1AA] hover:text-white"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleApplyTemplate}
                  disabled={applying}
                  className="bg-[#34C759] hover:bg-[#34C759]/90 text-white"
                  data-testid="confirm-apply-template-btn"
                >
                  {applying ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Aplicar Plantilla
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Save Custom Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Save className="w-5 h-5 text-[#34C759]" />
              Guardar como Plantilla
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Guarda tu configuración actual de sistemas como una plantilla reutilizable
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-[#A1A1AA]">Nombre de la plantilla</Label>
              <Input
                value={saveForm.name}
                onChange={(e) => setSaveForm({...saveForm, name: e.target.value})}
                placeholder="Ej: Mi configuración de iglesia"
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Descripción (opcional)</Label>
              <Input
                value={saveForm.description}
                onChange={(e) => setSaveForm({...saveForm, description: e.target.value})}
                placeholder="Ej: Configuración para servicio dominical"
                className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setShowSaveDialog(false)}
                className="text-[#A1A1AA] hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveCustomTemplate}
                className="bg-[#34C759] hover:bg-[#34C759]/90 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar Plantilla
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#111111] border-white/[0.08]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#F4F4F5]">
              Eliminar Plantilla
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#71717A]">
              ¿Estás seguro de eliminar "{selectedTemplate?.name}"? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-[#F4F4F5]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomTemplate}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
